import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download, QrCode, Share2, Edit, LogOut, CheckCircle2, Clock, XCircle, IndianRupee, Upload, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PlayerDashboard() {
  const nav = useNavigate();
  const [player, setPlayer] = useState(null);
  const [meta, setMeta] = useState({ tehsils: [], roles: [], jerseys: [] });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [settings, setSettings] = useState({});
  const [payEditing, setPayEditing] = useState(false);
  const [payUtr, setPayUtr] = useState("");
  const [payScreenshotId, setPayScreenshotId] = useState("");
  const [payUploading, setPayUploading] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payElapsed, setPayElapsed] = useState(0);

  const PAY_MIN_UTR = 12;
  const PAY_MIN_WAIT = 60;
  const payReady = !!payScreenshotId && payUtr.trim().length >= PAY_MIN_UTR && payElapsed >= PAY_MIN_WAIT;
  const payRemaining = Math.max(0, PAY_MIN_WAIT - payElapsed);
  const PAY_INCOMPLETE_MSG = "Please complete your payment first, upload a valid payment screenshot, and enter the UTR before submitting.";

  useEffect(() => {
    if (!payEditing) return;
    setPayElapsed(0);
    const iv = setInterval(() => setPayElapsed((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [payEditing]);

  const uploadPayScreenshot = async (file) => {
    if (!file) return;
    setPayUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "payments");
    try {
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayScreenshotId(res.data.file_id);
      toast.success("Screenshot uploaded");
    } catch { toast.error("Upload failed"); }
    setPayUploading(false);
  };
  const submitPayEdit = async () => {
    if (!payReady) { toast.error(PAY_INCOMPLETE_MSG); return; }
    setPaySubmitting(true);
    try {
      await api.post("/players/me/payment", { utr: payUtr, screenshot_id: payScreenshotId });
      toast.success("Payment resubmitted for verification");
      setPayEditing(false); setPayUtr(""); setPayScreenshotId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setPaySubmitting(false);
  };

  const load = async () => {
    try {
      const r = await api.get("/players/me");
      setPlayer(r.data); setForm(r.data);
    } catch { localStorage.removeItem("ndpl_player_token"); nav("/player/login"); }
  };
  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const saveProfile = async () => {
    try {
      const r = await api.put("/players/me", form);
      setPlayer(r.data); setEditing(false); toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const logout = () => { localStorage.removeItem("ndpl_player_token"); nav("/"); };
  const share = () => {
    const url = `${window.location.origin}/p/${player.public_slug}`;
    const text = encodeURIComponent(`Check my NDPL 2026 profile: ${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (!player) return <div className="min-h-screen bg-[#050505] grid place-items-center text-white/50">Loading…</div>;

  const statusPill = () => {
    const map = { approved: ["text-[#39FF14] border-[#39FF14]", <CheckCircle2 size={14} />], pending: ["text-[#FFD700] border-[#FFD700]", <Clock size={14} />], rejected: ["text-[#FF2442] border-[#FF2442]", <XCircle size={14} />] };
    const [cls, icon] = map[player.status] || map.pending;
    return <span data-testid="player-status" className={`inline-flex items-center gap-2 px-3 py-1 border ${cls} text-xs uppercase tracking-widest`}>{icon}{player.status}</span>;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Player Dashboard</div>
            <h1 className="font-display text-5xl md:text-6xl mt-2">Hey <span className="text-[#FFD700]">{player.name.split(" ")[0]}</span></h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="logout-btn" onClick={logout} className="px-4 py-2 border border-white/20 text-sm uppercase tracking-widest hover:border-[#FF2442] hover:text-[#FF2442] flex items-center gap-2"><LogOut size={14}/> Logout</button>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2 p-6 md:p-8 bg-[#0B132B] border border-white/10 rounded-lg">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-3xl">Profile</h2>
              {player.status !== "approved" && !editing && <button data-testid="edit-profile" onClick={() => setEditing(true)} className="text-xs uppercase tracking-widest text-[#39FF14] flex items-center gap-2"><Edit size={14} /> Edit</button>}
            </div>
            {editing ? (
              <div className="mt-5 grid md:grid-cols-2 gap-4">
                {[["Name","name"],["Age","age"]].map(([l,k]) => (
                  <label key={k} className="block"><span className="text-xs uppercase tracking-widest text-white/60">{l}</span>
                    <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: k === "age" ? +e.target.value : e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 px-4 py-3" /></label>
                ))}
                {[["Role","role",meta.roles],["Jersey","jersey_size",meta.jerseys],["Tehsil","tehsil",meta.tehsils]].map(([l,k,opts]) => (
                  <label key={k} className="block"><span className="text-xs uppercase tracking-widest text-white/60">{l}</span>
                    <select value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 px-4 py-3">
                      {opts.map((o) => <option key={o} className="bg-[#0B132B]" value={o}>{o}</option>)}
                    </select></label>
                ))}
                <div className="md:col-span-2 flex gap-3">
                  <button data-testid="save-profile" onClick={saveProfile} className="px-6 py-2 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs">Save</button>
                  <button onClick={() => { setEditing(false); setForm(player); }} className="px-6 py-2 border border-white/20 text-xs uppercase tracking-widest">Cancel</button>
                </div>
              </div>
            ) : (
              <dl className="mt-5 grid md:grid-cols-2 gap-y-4 gap-x-8">
                {[["Mobile", player.mobile],["Age", player.age],["Role", player.role],["Tehsil", player.tehsil],["Jersey", player.jersey_size],["Reg. Number", player.registration_number || "Pending"],["Trial Number", player.trial_number || "Pending"]].map(([l,v]) => (
                  <div key={l}><dt className="text-xs uppercase tracking-widest text-white/50">{l}</dt><dd className="text-lg mt-1">{v}</dd></div>
                ))}
              </dl>
            )}
          </div>

          {/* Status Card */}
          <div className="p-6 bg-gradient-to-br from-[#0B132B] to-[#151F3D] border border-white/10 rounded-lg space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Status</div>
              {statusPill()}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Payment</div>
              <div data-testid="payment-status" className="text-2xl font-display capitalize">
                {player.payment_status === "verified" ? <span className="text-[#39FF14]">Payment Verified</span> :
                 player.payment_status === "submitted" ? <span className="text-[#FFD700]">Under review</span> :
                 player.payment_status === "rejected" ? <span className="text-[#FF2442]">Rejected</span> :
                 <span className="text-white/60">Not Submitted</span>}
              </div>
              {player.payment_status !== "verified" && (
                <button
                  data-testid="edit-payment-btn"
                  onClick={() => { setPayUtr(player.utr || ""); setPayScreenshotId(player.screenshot_file_id || ""); setPayEditing(true); }}
                  className="mt-3 w-full flex items-center gap-2 justify-center px-4 py-2 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700] hover:text-black transition-colors uppercase tracking-widest text-xs">
                  <IndianRupee size={14}/> Edit Payment
                </button>
              )}
            </div>
            <div className="flex flex-col gap-3 pt-2 border-t border-white/10">
              <a href={`${API}/players/${player.id}/slip?token=${encodeURIComponent(localStorage.getItem("ndpl_player_token") || "")}`} target="_blank" rel="noreferrer" data-testid="download-slip" className="flex items-center gap-3 px-4 py-3 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors uppercase tracking-widest text-xs">
                <Download size={16}/> Download Slip
              </a>
              <a href={`${API}/players/${player.id}/qr`} target="_blank" rel="noreferrer" data-testid="download-qr" className="flex items-center gap-3 px-4 py-3 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700] hover:text-black transition-colors uppercase tracking-widest text-xs">
                <QrCode size={16}/> QR Player Card
              </a>
              <button onClick={share} data-testid="share-btn" className="flex items-center gap-3 px-4 py-3 bg-[#25D366] text-black hover:opacity-90 uppercase tracking-widest text-xs font-bold">
                <Share2 size={16}/> Share on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer settings={settings} />
      <FloatingWhatsapp />

      {payEditing && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={() => setPayEditing(false)} data-testid="edit-payment-modal">
          <div className="bg-[#0B132B] border border-white/15 max-w-md w-full p-6 rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#39FF14]">Update Payment</div>
                <h2 className="font-display text-3xl mt-1">₹{settings.registration_fee || 799}</h2>
              </div>
              <button onClick={() => setPayEditing(false)} className="text-white/60 hover:text-white"><X /></button>
            </div>

            {settings.upi_qr_file_id && (
              <div className="mt-4 p-3 bg-black/40 border border-white/10 text-center">
                <img src={fileUrl(settings.upi_qr_file_id)} alt="UPI QR" className="w-40 h-40 mx-auto object-contain" />
                <div className="mt-2 text-xs uppercase tracking-widest text-white/60">UPI ID</div>
                <div className="font-display text-lg text-[#39FF14]">{settings.upi_id || "ndplt10@upi"}</div>
              </div>
            )}

            <label className="block mt-4">
              <span className="text-xs uppercase tracking-widest text-white/60">UTR / Transaction ID</span>
              <input data-testid="edit-pay-utr" value={payUtr} onChange={(e) => setPayUtr(e.target.value)} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-4 py-3" placeholder="e.g. 123456789012" />
            </label>
            <label className="block mt-3">
              <span className="text-xs uppercase tracking-widest text-white/60">Payment Screenshot</span>
              <div className="mt-2 flex items-center gap-3">
                <label className="flex-1 flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer hover:border-[#39FF14]">
                  <Upload size={16} className="text-[#39FF14]" />
                  <span className="text-sm text-white/70 truncate">{payScreenshotId ? "Uploaded ✓" : "Choose image"}</span>
                  <input data-testid="edit-pay-screenshot" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPayScreenshot(e.target.files?.[0])} />
                </label>
                {payUploading && <span className="text-xs text-white/50">Uploading...</span>}
              </div>
            </label>
            <button data-testid="edit-pay-submit" onClick={submitPayEdit} disabled={paySubmitting || !payReady} className="mt-5 w-full py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
              {paySubmitting ? "Submitting…" : payRemaining > 0 ? `Wait ${payRemaining}s to submit` : "Submit for Verification"}
            </button>
            {!payReady && <div className="mt-2 text-xs text-white/50 text-center">Upload screenshot, enter UTR (min 12 chars){payRemaining > 0 ? `, and wait ${payRemaining}s` : ""} to enable submit.</div>}
            <div className="mt-3 text-xs text-white/50 text-center">Your registration stays <span className="text-[#FFD700]">Pending</span> until Admin verifies.</div>
          </div>
        </div>
      )}
    </div>
  );
}
